import React, { useState } from "react";
import { Ruler, ArrowRight, Info, Ban } from "lucide-react";
import { lookupOrbitecElectrode } from "@/data/orbitecElectrodeData";

// Localised copy. Kept in-file — one small widget, five languages.
const TR = {
  sk: { title: "Odporúčaná wolfrámová elektróda", subtitle: "Pre priemer rúry", hint: "Iba pre hlavy Orbitec — pre Axxair a Orbitalum použi vlastnú tabuľku.", openAll: "Zobraz celú tabuľku", none: "Pre tento priemer žiadna Orbitec hlava nie je kompatibilná.", nonOrbitec: "Vybral si stroj", nonOrbitecTail: ". Tieto dĺžky elektród platia IBA pre Orbitec hlavy — pre iné výrobky si over ich servisný manuál.", showOrbitec: "Zobraz Orbitec hodnoty" },
  en: { title: "Recommended tungsten electrode", subtitle: "For pipe Ø", hint: "Applies only to Orbitec heads — Axxair and Orbitalum use different values.", openAll: "Open full table", none: "No Orbitec head is compatible with this pipe diameter.", nonOrbitec: "You selected machine", nonOrbitecTail: ". These electrode lengths apply ONLY to Orbitec heads — for other brands consult their service manual.", showOrbitec: "Show Orbitec values" },
  cs: { title: "Doporučená wolframová elektroda", subtitle: "Pro průměr trubky", hint: "Platí pouze pro hlavy Orbitec — Axxair a Orbitalum používají jiné hodnoty.", openAll: "Otevřít celou tabulku", none: "Pro tento průměr není žádná Orbitec hlava kompatibilní.", nonOrbitec: "Vybral jsi stroj", nonOrbitecTail: ". Tyto délky elektrod platí POUZE pro Orbitec hlavy — pro jiné výrobce si ověř jejich servisní manuál.", showOrbitec: "Zobrazit Orbitec hodnoty" },
  de: { title: "Empfohlene Wolframelektrode", subtitle: "Für Rohr-Ø", hint: "Gilt nur für Orbitec-Köpfe — Axxair und Orbitalum verwenden andere Werte.", openAll: "Vollständige Tabelle öffnen", none: "Für diesen Rohrdurchmesser ist kein Orbitec-Kopf kompatibel.", nonOrbitec: "Du hast Maschine gewählt", nonOrbitecTail: ". Diese Elektrodenlängen gelten NUR für Orbitec-Köpfe — für andere Hersteller siehe deren Servicehandbuch.", showOrbitec: "Orbitec-Werte anzeigen" },
  pl: { title: "Zalecana elektroda wolframowa", subtitle: "Dla średnicy rury", hint: "Dotyczy tylko głowic Orbitec — Axxair i Orbitalum używają innych wartości.", openAll: "Otwórz pełną tabelę", none: "Dla tej średnicy rury żadna głowica Orbitec nie jest kompatybilna.", nonOrbitec: "Wybrałeś maszynę", nonOrbitecTail: ". Te długości elektrod dotyczą TYLKO głowic Orbitec — dla innych producentów sprawdź ich instrukcję serwisową.", showOrbitec: "Pokaż wartości Orbitec" },
  fr: {
  title: "Électrode tungstène recommandée",
  subtitle: "Pour diamètre du tube",
  hint: "Uniquement pour têtes Orbitec — pour Axxair et Orbitalum utilise leur propre tableau.",
  openAll: "Afficher le tableau complet",
  none: "Aucune tête Orbitec compatible pour ce diamètre.",
  nonOrbitec: "Tu as sélectionné la machine",
  nonOrbitecTail: ". Ces longueurs d'électrodes s'appliquent UNIQUEMENT aux têtes Orbitec — pour d'autres produits vérifie leur manuel de service.",
  showOrbitec: "Afficher les valeurs Orbitec"
},
};

/**
 * Compact panel shown next to the orbital calc result: given the currently
 * computed pipe diameter (mm), looks up the Orbitec electrode length table and
 * lists all compatible heads with their required stick-out length.
 *
 * Props:
 *   pipeMm:    number — pipe outside diameter
 *   machine:   "axxair" | "orbitec" | "orbitalum" | ... — currently selected machine
 *   lang:      current UI language
 *   onOpenTable: () => void — opens the full Orbitec reference table modal
 *
 * Behaviour:
 *   • machine === "orbitec"  → full auto-recommend panel with gold border
 *   • machine !== "orbitec"  → collapsed note ("values are Orbitec-only")
 *     that user can expand to see the recommendation anyway.
 */
export default function OrbitecElectrodeRecommend({ pipeMm, machine, lang = "sk", onOpenTable }) {
  const tr = TR[lang] || TR.sk;
  const [expanded, setExpanded] = useState(false);

  const result = lookupOrbitecElectrode(pipeMm);
  if (!result) return null; // out-of-range diameter — nothing to recommend

  const isOrbitec = (machine || "").toLowerCase() === "orbitec";
  const showData = isOrbitec || expanded;

  // Machine != Orbitec → soft note, user can expand.
  if (!isOrbitec && !expanded) {
    return (
      <div
        data-testid="orbitec-electrode-recommend-note"
        className="mt-4 border border-zinc-800 bg-zinc-950/60 p-3 text-xs text-zinc-400 flex items-start gap-2"
      >
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gold-500" />
        <div className="flex-1 leading-relaxed">
          {tr.nonOrbitec} <strong className="text-white">{machine}</strong>{tr.nonOrbitecTail}
        </div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="orbitec-electrode-recommend-expand"
          className="wb-btn-ghost !text-gold-300 !border-gold-800 hover:!bg-gold-950/40 px-3 py-1.5 text-[10px] uppercase tracking-widest whitespace-nowrap"
        >
          {tr.showOrbitec}
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="orbitec-electrode-recommend"
      className="mt-4 border border-gold-800/60 bg-gradient-to-br from-gold-950/25 to-transparent p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Ruler className="w-4 h-4 text-gold-400" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[3px] text-gold-400">Orbitec</div>
            <h3 className="font-display font-bold text-sm text-white leading-tight">{tr.title}</h3>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{tr.subtitle}</div>
          <div className="text-lg font-display font-bold text-gold-300 leading-none mt-0.5">
            {result.pipe_mm.toFixed(2)} <span className="text-xs text-zinc-500">mm</span>
          </div>
          <div className="text-[10px] text-zinc-500 font-mono">{result.pipe_inch.toFixed(3)}&quot;</div>
        </div>
      </div>

      {showData && result.matches.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-zinc-500 py-3">
          <Ban className="w-3.5 h-3.5" /> {tr.none}
        </div>
      ) : showData ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {result.matches.map((m) => (
            <div
              key={m.key}
              data-testid={`orbitec-electrode-recommend-${m.key}`}
              className="border border-zinc-800 bg-zinc-950/60 p-2.5 flex items-center justify-between gap-2 hover:border-gold-700/60 transition-colors"
            >
              <div className="min-w-0">
                <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 truncate">Head</div>
                <div className="text-xs font-semibold text-white truncate">{m.label}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-lg font-display font-black text-gold-300 leading-none">{m.mm}<span className="text-[10px] text-zinc-500 ml-0.5">mm</span></div>
                <div className="text-[10px] text-zinc-500 font-mono">{m.inch.toFixed(3)}&quot;</div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-zinc-800/60">
        <div className="text-[10px] text-zinc-500 leading-relaxed flex items-start gap-1.5">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-gold-500/80" />
          <span>{tr.hint}</span>
        </div>
        {onOpenTable && (
          <button
            type="button"
            onClick={onOpenTable}
            data-testid="orbitec-electrode-recommend-open-table"
            className="wb-btn-ghost !text-gold-300 !border-gold-800 hover:!bg-gold-950/40 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest whitespace-nowrap flex-shrink-0"
          >
            {tr.openAll} <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}
